// src/components/formularios/EvaluacionEditorDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  IconButton,
  Box,
  Typography,
  Chip,
  Divider,
  Paper,
  Checkbox,
  FormControlLabel,
  Alert,
  Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';
import {
  createFormulario,
  updateFormulario,
  getFormulario,
  listCompetencias,
  createCompetencia,
  type CreateFormularioDto,
  type CreatePreguntaDto,
  type CreateOpcionDto,
  type Competencia,
  type CompetenciaPesoDto,
} from '../../api/formularios';
import { useAuth } from '../../context/AuthContext';
import gorilaImg from '../../imagenes/Gorilas/gorila_formdialog.png';

interface Props {
  open: boolean;
  evaluacionId?: number;
  onClose: () => void;
  onSaved: () => void;
}

const ROLES_DISPONIBLES = [
  { value: 'staff', label: 'Staff (todos los departamentos - no profesor)' },
  { value: 'admin', label: 'Admin' },
  { value: 'att_familias', label: 'Atención a Familias' },
  { value: 'att_alumnado', label: 'Atención al Alumnado' },
  { value: 'att_profesorado', label: 'Atención al Profesorado' },
  { value: 'facturacion', label: 'Facturación' },
  { value: 'hr', label: 'Recursos Humanos' },
  { value: 'admisiones', label: 'Admisiones' },
  { value: 'profesor', label: 'Profesor' },
] as const;

type RoleValue = typeof ROLES_DISPONIBLES[number]['value'];

const DEPARTAMENTOS_STAFF: RoleValue[] = [
  'admin',
  'att_familias',
  'att_alumnado',
  'att_profesorado',
  'facturacion',
  'hr',
  'admisiones',
];

export default function EvaluacionEditorDialog({ open, evaluacionId, onClose, onSaved }: Props) {
  const { user } = useAuth();
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [estado, setEstado] = useState<'borrador' | 'activo' | 'desactivado' | 'cerrado'>(
    'activo',
  );
  const [rolesPermitidos, setRolesPermitidos] = useState<RoleValue[]>([]);
  const [puntuacionMaxima, setPuntuacionMaxima] = useState<number | ''>('');
  const [notaMinima, setNotaMinima] = useState<number | ''>('');
  const [numeroPreguntasMostrar, setNumeroPreguntasMostrar] = useState<number | ''>('');
  const [preguntas, setPreguntas] = useState<Array<CreatePreguntaDto & { tempId?: number }>>([]);
  const [competencias, setCompetencias] = useState<Competencia[]>([]);
  const [competenciasPesos, setCompetenciasPesos] = useState<CompetenciaPesoDto[]>([]);
  const [competenciaNueva, setCompetenciaNueva] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [tiempoLimite, setTiempoLimite] = useState<number | ''>('');

  useEffect(() => {
    if (open) {
      if (evaluacionId) {
        loadEvaluacion();
      } else {
        resetForm();
      }
    }
  }, [open, evaluacionId]);

  useEffect(() => {
    if (open) {
      listCompetencias()
        .then(setCompetencias)
        .catch(() => setCompetencias([]));
    }
  }, [open]);

  const loadEvaluacion = async () => {
    if (!evaluacionId) return;
    setLoadingForm(true);
    try {
      const evalData = await getFormulario(evaluacionId);
      setTitulo(evalData.titulo);
      setDescripcion(evalData.descripcion || '');
      setEstado(evalData.estado as any);
      setRolesPermitidos((evalData.rolesPermitidos || []) as RoleValue[]);
      setPuntuacionMaxima(evalData.puntuacionMaxima || '');
      setTiempoLimite(evalData.tiempoLimite || '');
      setNotaMinima(evalData.notaMinima || '');
      setNumeroPreguntasMostrar(evalData.numeroPreguntasMostrar || '');
      setCompetenciasPesos(
        (evalData.competenciasPesos || []).map((c: any) => ({
          competenciaId: c.competenciaId || c.competencia?.id,
          porcentaje: Number(c.porcentaje || 0),
        })),
      );
      if (evalData.preguntas) {
        setPreguntas(
          evalData.preguntas
            .sort((a, b) => (Number(a.orden) || 0) - (Number(b.orden) || 0))
            .map((p) => ({
              orden: Number(p.orden) || 1,
              tipo: p.tipo as 'test_seleccion_unica' | 'test_seleccion_multiple',
              pregunta: p.pregunta || '',
              requerida: p.requerida !== undefined ? Boolean(p.requerida) : true,
              puntosManuales:
                p.puntosManuales !== undefined && p.puntosManuales !== null
                  ? Number(p.puntosManuales)
                  : undefined,
              obligatoriaEnPool: Boolean(p.obligatoriaEnPool),
              competenciasIds: (p.competencias || []).map((c) => c.id),
              opciones: p.opciones
                ?.sort((a, b) => (Number(a.orden) || 0) - (Number(b.orden) || 0))
                .map((o, idx) => ({
                  texto: o.texto || '',
                  orden: Number(o.orden) || idx + 1,
                  esCorrecta: o.esCorrecta || false,
                })),
            })),
        );
      }
    } catch (error: any) {
      enqueueSnackbar('Error al cargar evaluación', { variant: 'error' });
    } finally {
      setLoadingForm(false);
    }
  };

  const resetForm = () => {
    setTitulo('');
    setDescripcion('');
    setEstado('activo');
    setRolesPermitidos([]);
    setPuntuacionMaxima('');
    setNotaMinima('');
    setNumeroPreguntasMostrar('');
    setPreguntas([]);
    setCompetenciasPesos([]);
    setCompetenciaNueva('');
  };

  const addPregunta = () => {
    const newOrden = preguntas.length > 0 ? Math.max(...preguntas.map((p) => p.orden)) + 1 : 1;
    setPreguntas([
      ...preguntas,
      {
        orden: newOrden,
        tipo: 'test_seleccion_unica',
        pregunta: '',
        requerida: true,
        tempId: Date.now(),
        opciones: [],
      },
    ]);
  };

  const removePregunta = (index: number) => {
    setPreguntas(preguntas.filter((_, i) => i !== index));
  };

  const updatePregunta = (index: number, updates: Partial<CreatePreguntaDto>) => {
    setPreguntas(preguntas.map((p, i) => (i === index ? { ...p, ...updates } : p)));
  };

  const addOpcion = (preguntaIndex: number) => {
    const pregunta = preguntas[preguntaIndex];
    const opciones = pregunta.opciones || [];
    const newOrden = opciones.length > 0 ? Math.max(...opciones.map((o) => o.orden)) + 1 : 1;
    updatePregunta(preguntaIndex, {
      opciones: [...opciones, { texto: '', orden: newOrden, esCorrecta: false }],
    });
  };

  const removeOpcion = (preguntaIndex: number, opcionIndex: number) => {
    const pregunta = preguntas[preguntaIndex];
    const opciones = pregunta.opciones || [];
    updatePregunta(preguntaIndex, {
      opciones: opciones.filter((_, i) => i !== opcionIndex),
    });
  };

  const updateOpcion = (
    preguntaIndex: number,
    opcionIndex: number,
    updates: Partial<CreateOpcionDto>,
  ) => {
    const pregunta = preguntas[preguntaIndex];
    const opciones = pregunta.opciones || [];
    updatePregunta(preguntaIndex, {
      opciones: opciones.map((o, i) => (i === opcionIndex ? { ...o, ...updates } : o)),
    });
  };

  // Calcular puntos automáticos
  const calcularPuntosAuto = () => {
    if (!puntuacionMaxima || typeof puntuacionMaxima !== 'number') return {};

    const totalPreguntasEvaluadas =
      typeof numeroPreguntasMostrar === 'number' && numeroPreguntasMostrar > 0
        ? numeroPreguntasMostrar
        : preguntas.length;

    if (totalPreguntasEvaluadas === 0) return {};

    const preguntasSinManual = preguntas.filter(
      (p) => p.puntosManuales === undefined || p.puntosManuales === null,
    );

    // Si no hay preguntas sin puntos manuales, todos los puntos son manuales
    if (preguntasSinManual.length === 0) {
      const puntos: Record<number, number> = {};
      preguntas.forEach((p, idx) => {
        puntos[idx] = p.puntosManuales || 0;
      });
      return puntos;
    }

    // Calcular puntos por pregunta: dividir la puntuación máxima entre el número de preguntas que se mostrarán
    const puntosPorPregunta = puntuacionMaxima / totalPreguntasEvaluadas;

    const puntos: Record<number, number> = {};

    preguntas.forEach((p, idx) => {
      if (p.puntosManuales !== undefined && p.puntosManuales !== null) {
        puntos[idx] = p.puntosManuales;
      } else {
        puntos[idx] = puntosPorPregunta;
      }
    });

    return puntos;
  };

  const totalPreguntasEvaluadas =
    typeof numeroPreguntasMostrar === 'number' && numeroPreguntasMostrar > 0
      ? numeroPreguntasMostrar
      : preguntas.length;
  
  const puntosCalculados = calcularPuntosAuto();
  
  // Validar puntos
  let puntosValidos = true;
  let sumaPuntos = 0;
  if (puntuacionMaxima && typeof puntuacionMaxima === 'number') {
    const puntosValores = Object.values(puntosCalculados);
    
    if (typeof numeroPreguntasMostrar === 'number' && numeroPreguntasMostrar > 0 && numeroPreguntasMostrar < preguntas.length) {
      // Cuando hay pool de preguntas: verificar que la suma de cualquier N preguntas sea igual a la puntuación máxima
      // Como todas las preguntas sin puntos manuales tienen el mismo valor (puntuacionMaxima / numeroPreguntasMostrar),
      // cualquier combinación de N preguntas sumará correctamente
      sumaPuntos = puntosValores.slice(0, totalPreguntasEvaluadas).reduce((a, b) => a + b, 0);
      puntosValidos = Math.abs(sumaPuntos - puntuacionMaxima) < 0.01;
    } else {
      // Sin pool: todas las preguntas se muestran, la suma debe igualar la puntuación máxima
      sumaPuntos = puntosValores.reduce((a, b) => a + b, 0);
      puntosValidos = Math.abs(sumaPuntos - puntuacionMaxima) < 0.01;
    }
  }

  const competenciasSeleccionadas = competencias.filter((c) =>
    competenciasPesos.some((p) => p.competenciaId === c.id),
  );

  const handleCompetenciasChange = (_: any, nuevos: Competencia[]) => {
    setCompetenciasPesos((prev) => {
      const map = new Map(prev.map((p) => [p.competenciaId, p.porcentaje]));
      return nuevos.map((c) => ({
        competenciaId: c.id,
        porcentaje: map.get(c.id) ?? 0,
      }));
    });
  };

  const handleSubmit = async () => {
    if (!titulo.trim()) {
      enqueueSnackbar('El título es obligatorio', { variant: 'warning' });
      return;
    }

    if (!puntuacionMaxima || typeof puntuacionMaxima !== 'number' || puntuacionMaxima <= 0) {
      enqueueSnackbar('La puntuación máxima es obligatoria y debe ser mayor a 0', {
        variant: 'warning',
      });
      return;
    }

    if (notaMinima && typeof notaMinima === 'number' && notaMinima > puntuacionMaxima) {
      enqueueSnackbar('La nota mínima no puede ser mayor que la puntuación máxima', {
        variant: 'warning',
      });
      return;
    }

    if (!puntosValidos) {
      enqueueSnackbar('La suma de puntos no coincide con la puntuación máxima', {
        variant: 'warning',
      });
      return;
    }

    if (competenciasPesos.length > 0) {
      const suma = competenciasPesos.reduce((acc, c) => acc + (c.porcentaje || 0), 0);
      if (suma > 100) {
        enqueueSnackbar('La suma de porcentajes de competencias no puede superar 100', {
          variant: 'warning',
        });
        return;
      }
    }

    // Validar que todas las preguntas tengan al menos una opción correcta
    for (const pregunta of preguntas) {
      if (!pregunta.opciones || pregunta.opciones.length === 0) {
        enqueueSnackbar(
          `La pregunta "${pregunta.pregunta || pregunta.orden}" debe tener al menos una opción`,
          { variant: 'warning' },
        );
        return;
      }
      const tieneCorrecta = pregunta.opciones.some((o) => o.esCorrecta);
      if (!tieneCorrecta) {
        enqueueSnackbar(
          `La pregunta "${pregunta.pregunta || pregunta.orden}" debe tener al menos una opción correcta`,
          { variant: 'warning' },
        );
        return;
      }
    }

    // Validar numeroPreguntasMostrar
    if (numeroPreguntasMostrar && typeof numeroPreguntasMostrar === 'number') {
      if (numeroPreguntasMostrar > preguntas.length) {
        enqueueSnackbar(
          'El número de preguntas a mostrar no puede ser mayor que el total de preguntas',
          { variant: 'warning' },
        );
        return;
      }
      if (numeroPreguntasMostrar <= 0) {
        enqueueSnackbar('El número de preguntas a mostrar debe ser mayor a 0', {
          variant: 'warning',
        });
        return;
      }

      const obligatoriasPool = preguntas.filter((p) => p.obligatoriaEnPool).length;
      if (obligatoriasPool > numeroPreguntasMostrar) {
        enqueueSnackbar(
          'Hay más preguntas obligatorias que el número de preguntas a mostrar',
          { variant: 'warning' },
        );
        return;
      }
    }

    setLoading(true);
    try {
      // Expandir "staff" a roles específicos
      let rolesFinales = [...rolesPermitidos];
      if (rolesPermitidos.includes('staff')) {
        rolesFinales = rolesFinales.filter((r) => r !== 'staff');
        rolesFinales.push(...DEPARTAMENTOS_STAFF);
      }

      const payload: CreateFormularioDto = {
        titulo: titulo.trim(),
        descripcion: descripcion.trim() || undefined,
        estado,
        esEvaluacion: true,
        rolesPermitidos: rolesFinales.length > 0 ? rolesFinales : undefined,
        puntuacionMaxima: typeof puntuacionMaxima === 'number' ? puntuacionMaxima : undefined,
        notaMinima: typeof notaMinima === 'number' ? notaMinima : undefined,
        tiempoLimite: typeof tiempoLimite === 'number' ? tiempoLimite : undefined,
        numeroPreguntasMostrar:
          typeof numeroPreguntasMostrar === 'number' ? numeroPreguntasMostrar : undefined,
        competenciasPesos: competenciasPesos.length > 0 ? competenciasPesos : undefined,
        preguntas: preguntas.map((p) => {
          const preguntaData: any = {
            orden: Number(p.orden) || 1,
            tipo: p.tipo,
            pregunta: p.pregunta.trim(),
            requerida: p.requerida !== undefined ? Boolean(p.requerida) : true,
          };

          if (p.puntosManuales !== undefined && p.puntosManuales !== null) {
            preguntaData.puntosManuales = Number(p.puntosManuales);
          }

          if (p.obligatoriaEnPool !== undefined) {
            preguntaData.obligatoriaEnPool = Boolean(p.obligatoriaEnPool);
          }

          if (p.opciones && p.opciones.length > 0) {
            preguntaData.opciones = p.opciones
              .filter((o) => o.texto && o.texto.trim())
              .map((o, idx) => ({
                texto: o.texto.trim(),
                orden: Number(o.orden) || idx + 1,
                esCorrecta: Boolean(o.esCorrecta),
              }));
          }

          if (p.competenciasIds && p.competenciasIds.length > 0) {
            preguntaData.competenciasIds = p.competenciasIds;
          }

          return preguntaData;
        }),
      };

      if (evaluacionId) {
        await updateFormulario(evaluacionId, payload);
        enqueueSnackbar('Evaluación actualizada', { variant: 'success' });
      } else {
        await createFormulario(payload);
        enqueueSnackbar('Evaluación creada', { variant: 'success' });
      }
      onSaved();
      onClose();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Error al guardar evaluación', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  if (loadingForm) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              backdropFilter: 'blur(8px)',
            },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            overflow: 'visible',
            ml: { xs: 0, md: '60px' },
          },
        }}
      >
        {/* Gorila lateral */}
        <Box
          component="img"
          src={gorilaImg}
          alt="Gorila"
          sx={{
            position: 'absolute',
            left: 0,
            top: { xs: '10px', sm: '-10px' },
            transform: 'translateX(-100%)',
            width: { xs: '70px', sm: '100px', md: '120px' },
            height: 'auto',
            zIndex: 1,
            display: { xs: 'none', sm: 'block' },
          }}
        />
        <DialogTitle
          sx={{
            backgroundColor: '#000',
            color: '#fff',
            fontWeight: 'bold',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
          }}
        >
          {evaluacionId ? 'Editar Evaluación' : 'Nueva Evaluación'}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#fff' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography>Cargando evaluación...</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(8px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'visible',
          ml: { xs: 0, md: '60px' },
        },
      }}
    >
      {/* Gorila lateral */}
      <Box
        component="img"
        src={gorilaImg}
        alt="Gorila"
        sx={{
          position: 'absolute',
          left: 0,
          top: { xs: '10px', sm: '-10px' },
          transform: 'translateX(-100%)',
          width: { xs: '70px', sm: '100px', md: '120px' },
          height: 'auto',
          zIndex: 1,
          display: { xs: 'none', sm: 'block' },
        }}
      />
      <DialogTitle
        sx={{
          backgroundColor: '#000',
          color: '#fff',
          fontWeight: 'bold',
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
        }}
      >
        {evaluacionId ? 'Editar Evaluación' : 'Nueva Evaluación'}
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: '#fff' }}>
        <Stack spacing={3}>
          <TextField
            label="Título *"
            fullWidth
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
          />
          <TextField
            label="Descripción"
            fullWidth
            multiline
            rows={2}
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
          />
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={estado}
                onChange={(e) => setEstado(e.target.value as any)}
                label="Estado"
              >
                <MenuItem value="borrador">Borrador</MenuItem>
                <MenuItem value="activo">Activo</MenuItem>
                <MenuItem value="desactivado">Desactivado</MenuItem>
                <MenuItem value="cerrado">Cerrado</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Puntuación Máxima *"
              type="number"
              fullWidth
              value={puntuacionMaxima}
              onChange={(e) => {
                const val = e.target.value === '' ? '' : Number(e.target.value);
                setPuntuacionMaxima(val);
              }}
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              label="Nota Mínima"
              type="number"
              fullWidth
              value={notaMinima}
              onChange={(e) => {
                const val = e.target.value === '' ? '' : Number(e.target.value);
                setNotaMinima(val);
              }}
              inputProps={{ min: 0, step: 0.01 }}
              helperText={
                notaMinima && typeof notaMinima === 'number' && notaMinima > (puntuacionMaxima || 0)
                  ? 'No puede ser mayor que la puntuación máxima'
                  : ''
              }
            />
            <TextField
              label="Tiempo Límite (minutos)"
              type="number"
              fullWidth
              value={tiempoLimite}
              onChange={(e) => {
                const val = e.target.value === '' ? '' : Number(e.target.value);
                setTiempoLimite(val);
              }}
              inputProps={{ min: 1 }}
              helperText="Tiempo en minutos para completar la evaluación (opcional)"
            />
          </Stack>

          <Autocomplete
            multiple
            options={ROLES_DISPONIBLES}
            getOptionLabel={(option) => option.label}
            value={ROLES_DISPONIBLES.filter((rol) => rolesPermitidos.includes(rol.value))}
            onChange={(_, newValue) => {
              const nuevosValores = newValue.map((rol) => rol.value) as RoleValue[];
              const seleccionoStaff = newValue.some((rol) => rol.value === 'staff');
              const tieneTodosDepts = DEPARTAMENTOS_STAFF.every((d) => nuevosValores.includes(d));

              // Si seleccionó la opción "staff", expandir a todos los departamentos (sin incluir 'profesor')
              if (seleccionoStaff && !tieneTodosDepts) {
                setRolesPermitidos([...DEPARTAMENTOS_STAFF]);
              } else if (!seleccionoStaff && rolesPermitidos.some((r) => DEPARTAMENTOS_STAFF.includes(r))) {
                // Si se deseleccionó la opción staff (o se quitaron departamentos), eliminar departamentos seleccionados
                setRolesPermitidos(nuevosValores.filter((r) => !DEPARTAMENTOS_STAFF.includes(r)));
              } else {
                setRolesPermitidos(nuevosValores);
              }
            }} 
            renderInput={(params) => (
              <TextField
                {...params}
                label="Roles Permitidos"
                placeholder={rolesPermitidos.length === 0 ? 'Todos los usuarios activos' : ''}
              />
            )}
            renderTags={(value) => {
              const allDeptsSelected = DEPARTAMENTOS_STAFF.every((d) => value.some((rol) => rol.value === d));

              if (allDeptsSelected) {
                // Mostrar un único chip "Staff (todos los departamentos - no profesores)" y los roles adicionales
                const otherRoles = value.filter((rol) => !DEPARTAMENTOS_STAFF.includes(rol.value as any));
                return (
                  <>
                    <Chip
                      key="staff-chip"
                      label="Staff (todos los departamentos - no profesor)"
                      onDelete={() => setRolesPermitidos((prev) => prev.filter((r) => !DEPARTAMENTOS_STAFF.includes(r)))}
                      sx={{ mr: 1 }}
                    />
                    {otherRoles.map((rol) => (
                      <Chip
                        key={rol.value}
                        label={rol.label}
                        onDelete={() => setRolesPermitidos((prev) => prev.filter((r) => r !== rol.value))}
                        sx={{ mr: 1 }}
                      />
                    ))}
                  </>
                );
              }

              return value.map((rol) => (
                <Chip
                  key={rol.value}
                  label={rol.label}
                  onDelete={() => setRolesPermitidos((prev) => prev.filter((r) => r !== rol.value))}
                  sx={{ mr: 1 }}
                />
              ));
            }}
            filterOptions={(options, params) => {
              const filtered = options.filter((option) => {
                const searchText = params.inputValue.toLowerCase();
                const label = option.label.toLowerCase();
                return label.includes(searchText);
              });
              return filtered;
            }}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            fullWidth
          />

          <Divider>
            <Typography variant="h6">Competencias</Typography>
          </Divider>

          <Stack spacing={2}>
            <Autocomplete
              multiple
              options={competencias}
              getOptionLabel={(option) => option.nombre}
              value={competenciasSeleccionadas}
              onChange={handleCompetenciasChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Competencias para distribución (%)"
                  placeholder="Selecciona competencias"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            {competenciasSeleccionadas.map((comp) => (
              <TextField
                key={comp.id}
                label={`% ${comp.nombre}`}
                type="number"
                value={
                  competenciasPesos.find((p) => p.competenciaId === comp.id)
                    ?.porcentaje ?? 0
                }
                onChange={(e) => {
                  const val = Number(e.target.value);
                  setCompetenciasPesos((prev) =>
                    prev.map((p) =>
                      p.competenciaId === comp.id
                        ? { ...p, porcentaje: val }
                        : p,
                    ),
                  );
                }}
                inputProps={{ min: 0, max: 100, step: 1 }}
              />
            ))}

            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                label="Nueva competencia"
                value={competenciaNueva}
                onChange={(e) => setCompetenciaNueva(e.target.value)}
                fullWidth
              />
              <Button
                variant="outlined"
                onClick={async () => {
                  const nombre = competenciaNueva.trim();
                  if (!nombre) return;
                  try {
                    const nueva = await createCompetencia({ nombre });
                    setCompetencias((prev) => {
                      const existe = prev.some((c) => c.id === nueva.id);
                      return existe ? prev : [...prev, nueva].sort((a, b) => a.nombre.localeCompare(b.nombre));
                    });
                    setCompetenciaNueva('');
                  } catch {
                    // ignore
                  }
                }}
              >
                Agregar
              </Button>
            </Stack>
          </Stack>

          <TextField
            label="Número de Preguntas a Mostrar (Pool)"
            type="number"
            fullWidth
            value={numeroPreguntasMostrar}
            onChange={(e) => {
              const val = e.target.value === '' ? '' : Number(e.target.value);
              setNumeroPreguntasMostrar(val);
            }}
            inputProps={{ min: 1 }}
            helperText={
              numeroPreguntasMostrar && typeof numeroPreguntasMostrar === 'number'
                ? `Se mostrarán ${numeroPreguntasMostrar} preguntas aleatorias de ${preguntas.length} totales`
                : 'Si se deja vacío, se mostrarán todas las preguntas'
            }
          />

          {!puntosValidos && (
            <Alert severity="error">
              La suma de puntos ({sumaPuntos.toFixed(2)}) no coincide con la puntuación máxima (
              {puntuacionMaxima})
            </Alert>
          )}

          <Divider>
            <Typography variant="h6">Preguntas Tipo Test</Typography>
          </Divider>

          {preguntas.map((pregunta, pIdx) => {
            const puntosPregunta = puntosCalculados[pIdx] || 0;
            return (
              <Paper
                key={pIdx}
                sx={{
                  p: 0,
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                {/* Header con primary.main */}
                <Box
                  sx={{
                    backgroundColor: 'primary.main',
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    position: 'relative',
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#fff' }}>
                    Pregunta {pregunta.orden}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => removePregunta(pIdx)}
                    sx={{
                      color: '#ffffff',
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.2)',
                        backgroundColor: 'rgba(211, 47, 47, 0.08)',
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Contenido de la pregunta */}
                <Box sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <TextField
                      label="Pregunta *"
                      fullWidth
                      value={pregunta.pregunta}
                      onChange={(e) => updatePregunta(pIdx, { pregunta: e.target.value })}
                    />
                    <Stack direction="row" spacing={2}>
                      <FormControl fullWidth>
                        <InputLabel>Tipo</InputLabel>
                        <Select
                          value={pregunta.tipo}
                          onChange={(e) => updatePregunta(pIdx, { tipo: e.target.value as any })}
                          label="Tipo"
                        >
                          <MenuItem value="test_seleccion_unica">Selección Única</MenuItem>
                          <MenuItem value="test_seleccion_multiple">Selección Múltiple</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Puntos Manuales"
                        type="number"
                        fullWidth
                        value={pregunta.puntosManuales || ''}
                        onChange={(e) => {
                          const val = e.target.value === '' ? undefined : Number(e.target.value);
                          updatePregunta(pIdx, { puntosManuales: val });
                        }}
                        inputProps={{ min: 0, step: 0.01 }}
                        helperText={
                          pregunta.puntosManuales === undefined || pregunta.puntosManuales === null
                            ? `Auto: ${puntosPregunta.toFixed(2)} pts`
                            : `Manual: ${pregunta.puntosManuales} pts`
                        }
                      />
                    </Stack>
                    <Autocomplete
                      multiple
                      options={competencias}
                      getOptionLabel={(option) => option.nombre}
                      value={competencias.filter((c) =>
                        (pregunta.competenciasIds || []).includes(c.id),
                      )}
                      onChange={(_, nuevos) =>
                        updatePregunta(pIdx, {
                          competenciasIds: nuevos.map((c) => c.id),
                        })
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Competencias" />
                      )}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(pregunta.obligatoriaEnPool)}
                          onChange={(e) =>
                            updatePregunta(pIdx, { obligatoriaEnPool: e.target.checked })
                          }
                        />
                      }
                      label="Incluir siempre en el pool"
                    />
                    <Typography variant="subtitle2">Opciones:</Typography>
                    {pregunta.opciones?.map((opcion, oIdx) => (
                      <Stack key={oIdx} direction="row" spacing={1} alignItems="center">
                        <TextField
                          label={`Opción ${oIdx + 1}`}
                          fullWidth
                          size="small"
                          value={opcion.texto}
                          onChange={(e) => updateOpcion(pIdx, oIdx, { texto: e.target.value })}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={opcion.esCorrecta || false}
                              onChange={(e) =>
                                updateOpcion(pIdx, oIdx, { esCorrecta: e.target.checked })
                              }
                            />
                          }
                          label="Correcta"
                        />
                        <IconButton size="small" onClick={() => removeOpcion(pIdx, oIdx)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => addOpcion(pIdx)}
                      startIcon={<AddIcon />}
                    >
                      Agregar Opción
                    </Button>
                  </Stack>
                </Box>
              </Paper>
            );
          })}

          <Button variant="outlined" onClick={addPregunta} startIcon={<AddIcon />} sx={{ backgroundColor: '#21A598', color: '#fff', '&:hover': { backgroundColor: '#1b8a85', } }}>
            Agregar Pregunta
          </Button>

          {puntuacionMaxima && typeof puntuacionMaxima === 'number' && (
            <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
              <Typography variant="subtitle2" gutterBottom>
                Resumen de Puntos:
              </Typography>
              <Typography variant="body2">Puntuación Máxima: {puntuacionMaxima} pts</Typography>
              <Typography variant="body2">Suma Actual: {sumaPuntos.toFixed(2)} pts</Typography>
              {puntosValidos ? (
                <Chip label="✓ Puntos válidos" color="success" size="small" sx={{ mt: 1 }} />
              ) : (
                <Chip label="✗ Puntos inválidos" color="error" size="small" sx={{ mt: 1 }} />
              )}
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          backgroundColor: '#fff',
          borderBottomLeftRadius: '12px',
          borderBottomRightRadius: '12px',
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: '#d32f2f',
            '&:hover': {
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
            },
          }}
        >
          Cancelar
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || !puntosValidos}>
          {loading ? 'Guardando...' : evaluacionId ? 'Actualizar' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
